import React, {
  type PropsWithChildren,
  cloneElement,
  useEffect,
  useRef,
} from 'react';
import cx from 'classnames';
import { UncontrolledPopover, UncontrolledPopoverProps } from 'reactstrap';
import { Button } from 'components';

import './Popover.scss';

export interface PopoverProps
  extends Pick<UncontrolledPopoverProps, 'placement'> {
  /** className for the trigger button */
  buttonClassName?: string;
  /** Custom trigger button */
  customButton?: JSX.Element;
  /** className for the popover */
  className?: string;
  /** Boolean to determine if the popover is disabled */
  isDisabled?: boolean;
  /** Stop propagation on trigger click (e.g., when nested inside links) */
  stopPropagation?: boolean;
  /** Unique ID required to link the popover to the trigger */
  id: string;
  /* Boolean used to update the zIndex of popovers inside modals */
  inModal?: boolean;
  /** Button/trigger contents */
  trigger: JSX.Element | string;
  /** Accessible label for the trigger button */
  triggerAriaLabel?: string;
  /** Method to trigger the popover */
  popoverTriggerMethod?: 'focus' | 'click' | 'legacy' | 'manual';
  /** Boolean to determine if the popover should fade in/out */
  fade?: boolean;
  /** Controlled popover state */
  isOpen?: boolean;
  /** Setter function for controlled popover state */
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  /** Whether controlled popovers should close when clicking outside */
  closeOnOutsideClick?: boolean;
}

/** Popover for showing additional info on click. Required for the color picker. */

const Popover: React.FC<PropsWithChildren<PopoverProps>> = props => {
  const {
    buttonClassName,
    customButton,
    className,
    children,
    isDisabled,
    id,
    inModal,
    placement = 'auto',
    stopPropagation = false,
    trigger,
    triggerAriaLabel,
    popoverTriggerMethod = 'focus',
    setIsOpen,
    closeOnOutsideClick = true,
    fade = false,
    ...remainingProps
  } = props;

  const popoverRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!closeOnOutsideClick || !setIsOpen) {
      return undefined;
    }

    const handleClick = (event: MouseEvent) => {
      if (
        !popoverRef.current?.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setIsOpen?.(false);
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [closeOnOutsideClick, popoverRef, setIsOpen]);

  return (
    <span>
      {customButton ? (
        cloneElement(customButton, { ref: buttonRef })
      ) : (
        <Button
          innerRef={buttonRef}
          className={cx('popover-btn', buttonClassName)}
          id={id}
          isPopoverTrigger
          tabIndex={-1}
          onClick={event => {
            if (stopPropagation) {
              event.preventDefault();
              event.stopPropagation();
            }
            setIsOpen?.(prevState => !prevState);
            event.currentTarget.focus();
          }}
          disabled={isDisabled}
          aria-label={triggerAriaLabel}
        >
          {trigger}
        </Button>
      )}
      <UncontrolledPopover
        {...remainingProps}
        placement={placement}
        target={id}
        trigger={popoverTriggerMethod}
        fade={fade}
        className={cx(className, { modalPopover: inModal })}
      >
        <div ref={popoverRef}>{children}</div>
      </UncontrolledPopover>
    </span>
  );
};

export default Popover;
